import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { MediaItem } from "@/types/media";

interface HeroCarouselProps {
  items: MediaItem[];
}

export default function HeroCarousel({ items }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    if (items.length <= 1) return;
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % items.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [items.length]);

  if (items.length === 0) return null;
  const active = items[index];

  return (
    <section className="relative w-full h-[540px] md:h-[620px] overflow-hidden bg-foreground-950">
      {items.map((item, i) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-1000 ${i === index ? "opacity-100" : "opacity-0"}`}
        >
          <img
            src={item.heroImage ?? item.poster}
            alt={`${item.title}剧照`}
            title={`${item.title} - 91n影视大全`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/55 to-black/20"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-transparent to-transparent"></div>
        </div>
      ))}

      <div className="relative z-10 w-full h-full px-4 md:px-8 flex items-center">
        <div key={active.id} className="w-full max-w-2xl animate-fade-up">
          <span className="inline-flex items-center gap-1.5 h-7 px-3 rounded-full bg-primary-500 text-background-50 text-xs md:text-sm font-semibold mb-4">
            <i className="ri-fire-fill"></i>
            {active.type === "movie" ? "最新上映" : "热播剧集"}
          </span>
          <h1 className="font-heading font-black text-white text-3xl md:text-5xl leading-tight mb-3">
            {active.title}
          </h1>
          <p className="text-primary-200 text-sm md:text-base font-medium mb-3">{active.tagline}</p>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-white/85 text-xs md:text-sm mb-4">
            <span className="flex items-center gap-1 text-secondary-300 font-semibold">
              <i className="ri-star-fill"></i>
              {active.rating.toFixed(1)}
            </span>
            <span>{active.year}</span>
            <span>{active.region}</span>
            <span>{active.genres.join(" / ")}</span>
            <span>{active.type === "movie" ? active.duration : active.updatedTo}</span>
          </div>
          <p className="text-white/75 text-sm md:text-[15px] leading-relaxed mb-6 max-w-xl line-clamp-3">
            {active.synopsis}
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => navigate(`/detail/${active.type}/${active.id}`)}
              className="h-11 px-6 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold flex items-center gap-2 whitespace-nowrap transition-colors"
            >
              <i className="ri-play-fill text-lg"></i>
              立即观看
            </button>
            <button
              type="button"
              onClick={() => navigate(`/detail/${active.type}/${active.id}`)}
              className="h-11 px-6 rounded-md bg-white/15 hover:bg-white/25 border border-white/25 text-white text-sm font-semibold flex items-center gap-2 whitespace-nowrap backdrop-blur transition-colors"
            >
              <i className="ri-bookmark-line text-lg"></i>
              查看详情
            </button>
          </div>
        </div>
      </div>

      <div className="absolute bottom-6 right-4 md:right-8 z-20 flex items-center gap-2">
        {items.map((item, i) => (
          <button
            key={item.id}
            type="button"
            aria-label={`切换到${item.title}`}
            onClick={() => setIndex(i)}
            className={`h-1.5 rounded-full transition-all duration-300 ${
              i === index ? "w-8 bg-primary-500" : "w-3 bg-white/50 hover:bg-white/80"
            }`}
          ></button>
        ))}
      </div>
    </section>
  );
}